/**
 * Component utility functions for common patterns
 * These utilities help reduce code duplication without changing component structure
 */

/**
 * Price formatting utilities
 */
export const priceUtils = {
  /**
   * Format price with proper currency formatting
   * @param {number} price - Price to format
   * @param {string} currency - Currency code (default: 'EUR')
   * @param {string} locale - Locale (default: 'nl-NL')
   * @returns {string} Formatted price
   */
  formatPrice(price, currency = 'EUR', locale = 'nl-NL') {
    if (typeof price !== 'number' || isNaN(price)) {
      return '0,00';
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  },

  /**
   * Format price without currency symbol
   * @param {number} price - Price to format
   * @param {string} locale - Locale (default: 'nl-NL')
   * @returns {string} Formatted price without currency
   */
  formatPriceWithoutCurrency(price, locale = 'nl-NL') {
    if (typeof price !== 'number' || isNaN(price)) {
      return '0,00';
    }

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  },

  /**
   * Round currency to proper decimal places
   * @param {number} amount - Amount to round
   * @returns {number} Rounded amount
   */
  roundCurrency(amount) {
    return Math.round(amount * 100) / 100;
  },
};

/**
 * Image handling utilities
 */
export const imageUtils = {
  /**
   * Get product image with fallback
   * @param {Object} product - Product object
   * @param {string} size - Image size (default: 'medium')
   * @returns {Object|null} Image object with src and alt
   */
  getProductImage(product, size = 'medium') {
    if (!product) return null;

    // Priority 1: images array
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const image = product.images[0];
      return {
        src: image[size] || image.medium || image.src || image,
        alt: product.name || 'Product image',
      };
    }

    // Priority 2: direct image property
    if (product.image && product.image !== 'false') {
      return {
        src: product.image,
        alt: product.name || 'Product image',
      };
    }

    return null;
  },

  /**
   * Get cart item image with multiple fallbacks
   * @param {Object} cartItem - Cart item object
   * @returns {Object|null} Image object with src and alt
   */
  getCartItemImage(cartItem) {
    if (!cartItem) return null;

    if (cartItem?.isBasePackage && cartItem?.basePackage?.image) {
      return {
        src: cartItem.basePackage.image,
        alt: cartItem.basePackage.title || cartItem?.packageName || 'Pakket',
      };
    }

    const pkgImg =
      cartItem?.packaging?.image ||
      cartItem?.package?.image ||
      cartItem?.packagingProduct?.image;
    if (pkgImg) {
      return {
        src: pkgImg,
        alt: cartItem?.packageName || 'Pakket',
      };
    }

    if (cartItem?.basePackage?.image) {
      return {
        src: cartItem.basePackage.image,
        alt: cartItem.basePackage.title || cartItem?.packageName || 'Pakket',
      };
    }
    const firstProductImg =
      cartItem?.products?.[0]?.image || cartItem?.foodDrinks?.[0]?.image;
    if (firstProductImg) {
      const name =
        cartItem?.products?.[0]?.name ||
        cartItem?.foodDrinks?.[0]?.name ||
        'Product';
      return { src: firstProductImg, alt: name };
    }

    return null;
  },
};

/**
 * HTML utilities
 */
export const htmlUtils = {
  /**
   * Decode HTML entities
   * @param {string} text - Text to decode
   * @returns {string} Decoded text
   */
  decodeHtml(text) {
    if (!text) return text;
    const div = document.createElement('div');
    div.innerHTML = text;
    return div.textContent || div.innerText || text;
  },

  /**
   * Escape HTML entities
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    if (!text) return text;
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },
};

/**
 * Device detection utilities
 */
export const deviceUtils = {
  /**
   * Check if device is mobile
   * @returns {boolean} True if mobile device
   */
  isMobile() {
    return window.innerWidth <= 768 || 'ontouchstart' in window;
  },

  /**
   * Check if device is tablet
   * @returns {boolean} True if tablet device
   */
  isTablet() {
    return window.innerWidth > 768 && window.innerWidth <= 1024;
  },

  /**
   * Check if device is desktop
   * @returns {boolean} True if desktop device
   */
  isDesktop() {
    return window.innerWidth > 1024;
  },

  /**
   * Get device type
   * @returns {string} Device type ('mobile', 'tablet', 'desktop')
   */
  getDeviceType() {
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    return 'desktop';
  },
};

/**
 * Validation utilities
 */
export const validationUtils = {
  /**
   * Validate email address
   * @param {string} email - Email to validate
   * @returns {boolean} True if valid email
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate phone number (Dutch format)
   * @param {string} phone - Phone to validate
   * @returns {boolean} True if valid phone
   */
  isValidPhone(phone) {
    const phoneRegex = /^(\+31|0)[1-9][0-9]{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  },

  /**
   * Validate Dutch postal code
   * @param {string} postcode - Postal code to validate
   * @returns {boolean} True if valid postal code
   */
  isValidPostcode(postcode) {
    const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{2}$/i;
    return postcodeRegex.test(postcode);
  },

  /**
   * Validate quantity
   * @param {number} quantity - Quantity to validate
   * @param {number} min - Minimum value (default: 1)
   * @param {number} max - Maximum value (default: 99999)
   * @returns {boolean} True if valid quantity
   */
  isValidQuantity(quantity, min = 1, max = 99999) {
    const qty = parseInt(quantity);
    return !isNaN(qty) && qty >= min && qty <= max;
  },
};

/**
 * Array utilities
 */
export const arrayUtils = {
  /**
   * Remove duplicates from array
   * @param {Array} array - Array to deduplicate
   * @param {string} key - Key to use for deduplication
   * @returns {Array} Deduplicated array
   */
  uniqueBy(array, key) {
    const seen = new Set();
    return array.filter((item) => {
      const value = key ? item[key] : item;
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  },

  /**
   * Group array by key
   * @param {Array} array - Array to group
   * @param {string} key - Key to group by
   * @returns {Object} Grouped object
   */
  groupBy(array, key) {
    return array.reduce((groups, item) => {
      const group = item[key];
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    }, {});
  },

  /**
   * Sort array by key
   * @param {Array} array - Array to sort
   * @param {string} key - Key to sort by
   * @param {string} direction - Sort direction ('asc' or 'desc')
   * @returns {Array} Sorted array
   */
  sortBy(array, key, direction = 'asc') {
    return array.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (direction === 'desc') {
        return bVal > aVal ? 1 : -1;
      }
      return aVal > bVal ? 1 : -1;
    });
  },
};

/**
 * Storage utilities
 */
export const storageUtils = {
  /**
   * Safe localStorage get with error handling
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} Stored value or default
   */
  getItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  },

  /**
   * Safe localStorage set with error handling
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   * @returns {boolean} True if successful
   */
  setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Error writing localStorage key "${key}":`, error);
      return false;
    }
  },

  /**
   * Safe localStorage remove with error handling
   * @param {string} key - Storage key
   * @returns {boolean} True if successful
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  },
};

/**
 * Event utilities
 */
export const eventUtils = {
  /**
   * Dispatch custom event with error handling
   * @param {string} eventName - Event name
   * @param {*} detail - Event detail
   * @returns {boolean} True if successful
   */
  dispatchEvent(eventName, detail = null) {
    try {
      const event = new CustomEvent(eventName, { detail });
      document.dispatchEvent(event);
      return true;
    } catch (error) {
      console.warn(`Error dispatching event "${eventName}":`, error);
      return false;
    }
  },

  /**
   * Add event listener with automatic cleanup
   * @param {string} eventName - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event options
   * @returns {Function} Cleanup function
   */
  addEventListener(eventName, handler, options = {}) {
    try {
      document.addEventListener(eventName, handler, options);
      return () => document.removeEventListener(eventName, handler, options);
    } catch (error) {
      console.warn(`Error adding event listener "${eventName}":`, error);
      return () => {};
    }
  },
};

/**
 * Error handling utilities
 */
export const errorUtils = {
  /**
   * Handle API errors with user-friendly messages
   * @param {Error} error - Error object
   * @param {string} context - Error context
   * @returns {string} User-friendly error message
   */
  handleApiError(error, context = 'API call') {
    if (error.name === 'NetworkError' || !navigator.onLine) {
      return 'Netwerkfout. Controleer je internetverbinding.';
    }

    if (error.status === 404) {
      return 'De gevraagde informatie kon niet worden gevonden.';
    }

    if (error.status === 500) {
      return 'Er is een serverfout opgetreden. Probeer het later opnieuw.';
    }

    if (error.status === 403) {
      return 'Je hebt geen toegang tot deze functie.';
    }

    return error.message || `Er is een fout opgetreden bij ${context}.`;
  },

  /**
   * Log error with context
   * @param {Error} error - Error object
   * @param {string} context - Error context
   * @param {Object} additionalData - Additional data to log
   */
  logError(error, context, additionalData = {}) {
    console.error(`[${context}]`, {
      message: error.message,
      stack: error.stack,
      ...additionalData,
    });
  },
};
